import { Fragment, useCallback, useEffect, useState } from 'react';
import { tw } from 'twind';
import {
  InputSelect,
  Button,
  Heading,
  Stack,
  Container,
  Icon,
  Text,
} from '@global-ecom/nitro-uds/elements';
import { useRouter } from 'next/router';

import { useCart } from 'hooks/useCart';
import { useMiniCart } from 'hooks/useMiniCart';
import { usePdp } from 'hooks/usePdp';
import { useProductCustomization } from 'hooks/useProductCustomization';
import { useTranslate } from 'hooks/useTranslations';
import { AnalyticsEvents, event } from 'utils/analytics';
import { Dimensions } from 'types/analyticTypes';
import { countingNumbersAsDropdownList } from 'utils/countingNumbers';
import { extractCode, extractError } from 'utils/extractError';
import {
  ProductFitOptions,
  ProductWishlistButton,
} from 'ui/pages/ProductDetailsPages';
import { getStockMessageFromResultByProductId } from 'utils/getStockMessageFromResultByProductId';
import { getMaxQuantity, getSizesFromSizeGroups } from 'utils/products';
import { ProductCartItem, Variant } from '__generated__/graphql';
import Modal from 'ui/components/Modal';
import CloseButton from 'ui/components/CloseButton';
import { useStickyButton } from 'hooks/useStickyButton';
import { SizeGuide } from 'ui/components/Sizes';
import { AddToCartButton } from 'ui/components/AddToCartButton';
import { getIsMobile } from 'utils/media';
import { REFERRER_TYPE } from 'utils/constants';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { useMainNavContext } from 'hooks/useMainNav';
import { isAppExclusiveValidRange } from 'utils/isAppExclusiveValidRange';
import { useFeature } from 'hooks/useFeature';
import { usePopupsOpenStore } from 'store';

import { ProductSizePicker } from './ProductSizePicker';
import { CartButtonPopup } from './CartButtonPopup';

type ProductActionsType = {
  inventory: number | undefined;
  mixedCartNotAllowed: boolean | undefined;
};

export const ProductActions = ({
  inventory,
  mixedCartNotAllowed,
}: ProductActionsType) => {
  const [addingToCart, setAddingToCart] = useState(false);

  const translate = useTranslate();
  const { addToCart, cart } = useCart();
  const { openMiniCart, setMixedCartNotAllowed } = useMiniCart();
  const router = useRouter();
  const { pageviewEventHasFired } = usePageEventsContext();
  const { setPopupOpen } = usePopupsOpenStore();

  const { showStickyButton, showSizesModal, setShowSizesModal } =
    useStickyButton();
  const { isSearchFormOpen, mobileMenuOpen } = useMainNavContext();

  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [popularityCount, setPopularityCount] = useState(0);

  const showGiftWithPurchaseModalInMobile = useFeature(
    'SHOW_GIFT_WITH_PURCHASE_MODAL_IN_MOBILE'
  );
  const isGiftWithPurchaseNewDesign = useFeature(
    'GIFT_WITH_PURCHASE_NEW_DESIGN'
  );

  const productCount = cart?.productCount || 0;

  const {
    desiredSize,
    desiredSwatch,
    isAppOnly,
    isFullLayout,
    isSimplifiedLayout,
    masterProduct,
    productIsOrderable,
    quantity,
    setAddToCartError,
    setAddToCartReturnCode,
    setQuantity,
    variation,
  } = usePdp();

  const { userProductCustomization } = useProductCustomization();

  const addItemsToCart = useCallback(async () => {
    if (!desiredSize) {
      setAddToCartError(translate('pleaseSelectSize'));
      return;
    }
    setAddingToCart(true);
    const sizes = getSizesFromSizeGroups(variation!.sizeGroups);
    const selectedSize = sizes.find(x => x.id === desiredSize);
    const currentUrl = router.asPath;

    const productDiscovery =
      window.localStorage.getItem(REFERRER_TYPE) ?? undefined;

    const res = await addToCart(
      {
        swatch: variation!.colorValue,
        size: selectedSize?.id,
        quantity,
        productId: selectedSize?.productId as string,
        productCustomization: userProductCustomization ?? undefined,
        masterId: variation!.masterId,
      },
      {
        ...(productDiscovery && { [Dimensions.d100]: productDiscovery }),
      },
      // DO NOT SHOW miniCart Modal in mobile
      !getIsMobile(),
      currentUrl
    );

    extractCode(res)
      ? setAddToCartReturnCode(extractCode(res))
      : setAddToCartReturnCode(
          getStockMessageFromResultByProductId(
            selectedSize?.productId as string,
            res.data?.addItemsToCart.products
          )
        );

    if (extractError(res)) {
      setAddToCartError(extractError(res));
      return setAddingToCart(false);
    }
    const items = res.data?.addItemsToCart.products?.filter(
      product =>
        product.__typename === 'ProductCartItem' &&
        product?.product.id === selectedSize?.productId
    ) as ProductCartItem[];

    if (items[0].popularityCount) setPopularityCount(items[0].popularityCount);

    // there is some gwp items
    if (res.data?.addItemsToCart.giftWithPurchases?.length) {
      // the miniCart handles the logic to open the GWPModal
      if (getIsMobile()) {
        if (isGiftWithPurchaseNewDesign || showGiftWithPurchaseModalInMobile) {
          openMiniCart(items); // the miniCarts redirect to open gwp modal
        } else {
          // in old design or no gwpInMobile -> only open popup
          setPopupOpen('CartButtonPopup', true);
        }
      } else {
        // in desktop open GWPModal
        setMixedCartNotAllowed(mixedCartNotAllowed!);
        openMiniCart(items);
      }
    } else {
      if (getIsMobile()) {
        setPopupOpen('CartButtonPopup', true);
      } else {
        // there is no gwp items but need to open mini cart anyways
        setMixedCartNotAllowed(mixedCartNotAllowed!);
        openMiniCart(items);
      }
    }

    setAddingToCart(false);

    event(AnalyticsEvents.GA4_CustomEvent, {
      event_name: AnalyticsEvents.GA4EC_Stylitics,
      event_params: {
        user_action: AnalyticsEvents.GA4_ADD_TO_CART,
        item_id_ep: variation?.id, // The ID of the item presented on the Product page on which a user interacts with the Stylitics feature
        item_name_ep: variation?.name, // The name of the item presented on the Product page on which a user interacts with the Stylitics feature
        stylitics_outfit_id: undefined, // this field can be undefined in the cases we cannot get the outfit ID
        stylitics_product_id: variation?.id, // if (user_action === "add_to_cart") => stylitics_product_id: "the ID of the item a user is currently interacting with within the Stylitics feature"
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addToCart,
    desiredSize,
    openMiniCart,
    quantity,
    setAddToCartError,
    translate,
    userProductCustomization,
    variation,
    setAddToCartReturnCode,
  ]);

  const selectedSwatch = masterProduct?.variations.find(
    item => item.colorValue === desiredSwatch
  ) as Variant;
  const sizes = selectedSwatch
    ? getSizesFromSizeGroups(selectedSwatch!.sizeGroups)
    : [];
  const selectedSize = sizes.find(item => item.id === desiredSize);

  const maxQuantity = getMaxQuantity({
    maxOrderableQuantity: selectedSize?.maxOrderableQuantity,
    inventoryQuantity: inventory,
  });

  const handleInputOnChange = newQty => {
    setQuantity(Number.parseInt(newQty as string));
    if (pageviewEventHasFired) {
      event(AnalyticsEvents.GA4_CustomEvent, {
        event_name: AnalyticsEvents.QUANTITY_CHANGE,
        event_params: {
          item_id_ep: selectedSwatch?.id,
          item_name_ep: selectedSwatch?.name,
        },
      });
    }
  };

  useEffect(() => {
    if (desiredSize || variation) {
      setQuantity(1);
    }
  }, [setQuantity, desiredSize, variation]);

  useEffect(() => {
    if (desiredSize && showSizesModal) {
      setShowSizesModal(false);
      addItemsToCart();
    }
  }, [addItemsToCart, desiredSize, setShowSizesModal, showSizesModal]);

  if (!productIsOrderable) return null;

  const desiredSizeIsOrderable =
    desiredSize === undefined ||
    sizes.find(size => size.id === desiredSize)?.orderable;

  return (
    <Stack direction="inline" gap="sm">
      {isFullLayout &&
        !isAppExclusiveValidRange(
          isAppOnly,
          variation?.appOnlyDateTimeFrom,
          variation?.appOnlyDateTimeTo
        ) && (
          <div className="w-24">
            <InputSelect
              id="qty-dropdown"
              label={translate('quantity')}
              showLabel={false}
              placeholder={translate('qty')}
              options={countingNumbersAsDropdownList(maxQuantity)}
              value={quantity.toString()}
              disabled={!desiredSize}
              onChange={handleInputOnChange}
              dataTestId="pdp-quantity-select"
            />
          </div>
        )}
      <Stack
        gap="sm"
        className={tw({ 'flex-row-reverse': isSimplifiedLayout })}
      >
        <AddToCartButton
          disabled={!desiredSizeIsOrderable}
          isAppExclusive={variation?.isAppExclusive}
          loading={addingToCart}
          onAddToCart={addItemsToCart}
          presale={variation?.displayOutOfStock?.presale}
          size="lg"
          appOnlyDateTimeFrom={variation?.appOnlyDateTimeFrom}
          appOnlyDateTimeTo={variation?.appOnlyDateTimeTo}
        />
        {!isAppExclusiveValidRange(
          isAppOnly,
          variation?.appOnlyDateTimeFrom,
          variation?.appOnlyDateTimeTo
        ) && (
          <ProductWishlistButton
            disabled={!desiredSizeIsOrderable}
            quantity={quantity}
          />
        )}
        <Container
          className={`sm:hidden fixed bottom-0 right-0 w-full bg-white transition z-40 duration-[1500ms]
        ${
          showStickyButton &&
          !isAppExclusiveValidRange(
            isAppOnly,
            variation?.appOnlyDateTimeFrom,
            variation?.appOnlyDateTimeTo
          )
            ? '-translate-y-0'
            : 'translate-y-full'
        }`}
        >
          {showSizeGuide && (
            <SizeGuide
              categoryId={masterProduct?.primaryCategoryId}
              productMeasurements={masterProduct?.productMeasurements}
              onClickOutside={() => {
                setShowSizeGuide(false);
                setShowSizesModal(true);
              }}
              productDivision={masterProduct?.productDivision}
            />
          )}
          {showSizesModal ? (
            <Modal
              variant="dark"
              onClickOutside={() => {
                setShowSizesModal(false);
              }}
              className="w-full animate-fade-in w-full rounded-none fixed bottom-0"
              containerClassName="p-0"
              dataTestId="size-modal"
            >
              <Stack
                direction="row"
                className="justify-between items-center px-6 py-4"
              >
                <Heading size="sm">{translate('pleaseSelectSize')}</Heading>

                <CloseButton
                  data-test-id="close-sticky-addtocart"
                  onClick={() => setShowSizesModal(false)}
                />
              </Stack>
              <Stack className="px-6 pb-6">
                <div onClick={() => setShowSizeGuide(true)}>
                  <Stack direction="row" items="center" gap="none">
                    <Text
                      size="sm"
                      weight="bold"
                      dataTestId="size-guide-button"
                    >
                      {translate('sizeGuide')}
                    </Text>
                    <Icon name="ruler" size="2xl" />
                  </Stack>
                </div>
                <ProductFitOptions />
                <ProductSizePicker isModalSizeOpen={true} />
              </Stack>
            </Modal>
          ) : (
            <Fragment>
              {!isSearchFormOpen && !mobileMenuOpen && (
                <Stack className="px-4 py-6">
                  <Button
                    label={
                      variation?.displayOutOfStock?.presale
                        ? translate('preOrder')
                        : translate('addToCart')
                    }
                    dataTestId="add-to-cart-sticky-button"
                    variant="primary"
                    className="w-full"
                    size="lg"
                    disabled={!desiredSizeIsOrderable}
                    loading={addingToCart}
                    onClick={() => {
                      if (sizes.length === 1 || selectedSize)
                        return addItemsToCart();
                      setShowSizesModal(true);
                    }}
                  />
                </Stack>
              )}
            </Fragment>
          )}
        </Container>
      </Stack>
      <CartButtonPopup {...{ popularityCount, productCount }} />
    </Stack>
  );
};
